import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { SearchHit } from '@proerp/shared';
import { Badge, cn, Skeleton } from '@proerp/ui';
import { keyLabels, type KeyPlatform } from '@/lib/platformKeys';
import { SHORTCUT_KEYS } from '@/lib/shortcuts';
import type { AnswerFact, CopyTarget, FactTone, HitAction } from './hitAnswer';
import type { HitAnswerState } from './useHitAnswer';
import { formatSimilarity, isSimilarHit, RESOURCE_ICON, searchBadgeLabel } from './searchMeta';
import { renderSnippet } from './searchSnippet';
import { isPlainLeftClick, usePaletteNavigate } from './usePaletteNavigate';

const TONE_TEXT: Record<FactTone, string> = {
  // Nur die Theme-Stufen `-500` sind definiert (hell/dunkel je eigener Wert).
  success: 'text-success-500',
  warning: 'text-warning-500',
  danger: 'text-danger-500',
};

/** Tasten-Hinweis (kbd-Optik), wie im Fuß der Palette. */
export function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex min-w-[1.4rem] items-center justify-center rounded border border-border bg-muted px-1 py-0.5 font-sans text-[0.6875rem] text-muted-foreground">
      {children}
    </kbd>
  );
}

/** Kürzel einer Aktion — nur die drei, die die Palette selbst versteht. */
function actionKeys(action: HitAction, copy: CopyTarget | null): readonly string[] | null {
  if (action.kind === 'open') return ['enter'];
  if (action.kind === 'background') return SHORTCUT_KEYS.paletteBackgroundTab;
  if (action.kind === 'copy' && copy && action.copy.value === copy.value) return SHORTCUT_KEYS.copy;
  return null;
}

/** ID eines Aktions-Elements — für `aria-activedescendant`-freie Ansage und Tests. */
export function actionElementId(index: number): string {
  return `palette-action-${index}`;
}

function FactValue({ fact, onCopy }: { fact: AnswerFact; onCopy: (copy: CopyTarget) => void }) {
  return (
    <dd className={cn('flex min-w-0 items-baseline justify-end gap-2 text-right font-medium text-foreground', fact.tone && TONE_TEXT[fact.tone])}>
      <span className="min-w-0 break-words">{fact.value}</span>
      {fact.copyValue && (
        <button
          type="button"
          tabIndex={-1}
          onClick={() => onCopy({ value: fact.copyValue ?? '', what: fact.label })}
          className="shrink-0 rounded px-1 text-xs font-normal text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          aria-label={`${fact.label} kopieren`}
        >
          kopieren
        </button>
      )}
    </dd>
  );
}

/**
 * Rechte Spalte der Suche (Vorschlag C „Antworten & Aktionen"): Kopf, die eine
 * Antwort groß, weitere Fakten, darunter „Was du tun kannst". Die markierte Aktion
 * (`activeIndex`) steuert die Palette per Tastatur (→/Tab hinein, ↑↓, ←/Esc
 * zurück, ↵ ausführen = Klick auf das Element mit `data-action-index`).
 *
 * Sprünge sind echte Anker (CLAUDE.md §9): Rechtsklick, ⌘-Klick und Ziel-URL in der
 * Statusleiste funktionieren. Nach normalem Klick schließt die Palette.
 */
export function HitAnswerCard({
  hit,
  groupLabel,
  state,
  activeIndex,
  keyPlatform,
  onNavigate,
  onBackground,
  onCopy,
}: {
  hit: SearchHit;
  groupLabel: string;
  state: HitAnswerState;
  activeIndex: number | null;
  keyPlatform: KeyPlatform;
  onNavigate: () => void;
  onBackground: (to: string) => void;
  onCopy: (copy: CopyTarget) => void;
}) {
  const { openFromClick } = usePaletteNavigate();
  const listRef = useRef<HTMLUListElement>(null);
  const Icon = RESOURCE_ICON[hit.type];

  // Die markierte Aktion bleibt sichtbar, auch wenn die Liste scrollt.
  useEffect(() => {
    if (activeIndex == null) return;
    listRef.current?.querySelector(`[data-action-index="${activeIndex}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);
  const { answer, isLoading, isError } = state;
  const badge = searchBadgeLabel(hit);
  const similarity = formatSimilarity(hit);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Antwort und Fakten scrollen; die Aktionen stehen unten fest — sie sind der
          Grund, die Karte per Tastatur zu betreten, und dürfen nie unter dem Rand liegen. */}
      <div className="min-h-0 flex-1 overflow-y-auto p-5 pb-3">
        <div className="flex items-start gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
            <Icon className="size-5" strokeWidth={1.5} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <span className="text-[0.6875rem] font-medium uppercase tracking-wider text-muted-foreground">{groupLabel}</span>
            <h3 className="break-words text-base font-semibold leading-snug tabular-nums text-foreground">{hit.title}</h3>
            {hit.subtitle && <p className="mt-0.5 break-words text-sm text-muted-foreground">{hit.subtitle}</p>}
          </div>
          {badge && <Badge className="shrink-0">{badge}</Badge>}
        </div>

        {/* Die Vorschau zeigt einen Treffer ohne seinen Abschnitt — deshalb steht
            hier noch einmal, dass es nur ein ähnlicher Treffer ist. */}
        {isSimilarHit(hit) && (
          <span className="mt-3 flex flex-col items-start gap-1">
            <Badge tone="outline">Ähnlicher Treffer{similarity ? ` · ${similarity}` : ''}</Badge>
            <span className="text-xs text-muted-foreground">Nicht genau dein Suchbegriff, nur ähnlich geschrieben.</span>
          </span>
        )}
        {hit.snippet && (
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">…{renderSnippet(hit.snippet)}…</p>
        )}

        {answer?.headline && (
          <div className="mt-4 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
            <div className="text-xs text-muted-foreground">{answer.headline.label}</div>
            <div
              className={cn(
                'text-xl font-semibold tabular-nums text-foreground',
                answer.headline.tone && TONE_TEXT[answer.headline.tone],
              )}
            >
              {answer.headline.value}
            </div>
          </div>
        )}
        {answer && answer.facts.length > 0 && (
          <dl className="mt-3 flex flex-col">
            {answer.facts.map((fact) => (
              <div
                key={fact.label}
                className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-3 border-b border-border/60 py-1.5 text-sm last:border-b-0"
              >
                <dt className="text-muted-foreground">{fact.label}</dt>
                <FactValue fact={fact} onCopy={onCopy} />
              </div>
            ))}
          </dl>
        )}
        {isLoading && (
          <div className="mt-4 flex flex-col gap-2" aria-label="Details werden geladen">
            {[0, 1, 2].map((key) => (
              <Skeleton key={key} className="h-5 w-full" />
            ))}
          </div>
        )}
        {isError && !isLoading && (
          <p className="mt-4 text-sm text-danger-500">Details konnten nicht geladen werden — öffnen geht trotzdem.</p>
        )}
      </div>

      {answer && (
        // Höchstens die halbe Karte — sonst verdrängen sieben Aktionen auf einem
        // 720-px-Bildschirm jede Antwort. Darüber scrollt die Liste selbst.
        <div className="flex max-h-[50%] shrink-0 flex-col border-t border-border px-5 pt-2.5 pb-3">
          <div className="mb-1 text-[0.6875rem] font-medium uppercase tracking-wider text-muted-foreground">
            Was du tun kannst
          </div>
          <ul ref={listRef} className="-mx-1 flex min-h-0 flex-col gap-0.5 overflow-y-auto px-1" aria-label="Aktionen">
            {answer.actions.map((action, index) => {
              const keys = actionKeys(action, answer.copy);
              const isActive = index === activeIndex;
              const ActionIcon = action.icon;
              const body = (
                <>
                  <ActionIcon className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.5} aria-hidden />
                  <span className="flex min-w-0 flex-1 flex-col text-left">
                    <span className="truncate">{action.label}</span>
                    {/* Der Zusatz nur an der markierten Aktion — hält die Liste kurz. */}
                    {action.hint && isActive && <span className="truncate text-xs text-muted-foreground">{action.hint}</span>}
                  </span>
                  {keys && (
                    <span className="flex shrink-0 items-center gap-0.5" aria-hidden>
                      {keyLabels(keys, keyPlatform).map((label) => (
                        <Kbd key={label}>{label}</Kbd>
                      ))}
                    </span>
                  )}
                </>
              );
              const common = {
                id: actionElementId(index),
                title: action.hint,
                'data-action-index': index,
                'data-active': isActive || undefined,
                tabIndex: -1,
                className: cn(
                  'flex w-full min-w-0 items-center gap-2.5 rounded-md px-2 py-1 text-sm text-foreground no-underline outline-none transition-colors hover:bg-muted',
                  isActive && 'bg-muted ring-1 ring-ring/40',
                ),
              };
              let element: React.ReactNode;
              if (action.kind === 'open' || action.kind === 'link') {
                const to = action.to;
                element = (
                  <Link
                    {...common}
                    to={to}
                    onClick={(event) => {
                      if (!isPlainLeftClick(event)) return;
                      openFromClick(event, to);
                      onNavigate();
                    }}
                  >
                    {body}
                  </Link>
                );
              } else if (action.kind === 'external') {
                element = (
                  <a {...common} href={action.href}>
                    {body}
                  </a>
                );
              } else {
                const run = action.kind === 'background' ? () => onBackground(action.to) : () => onCopy(action.copy);
                element = (
                  <button type="button" {...common} onClick={run}>
                    {body}
                  </button>
                );
              }
              return <li key={action.id}>{element}</li>;
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
